import React from 'react';
import { ErrorMessage, useFormikContext } from 'formik';
import clsx from 'clsx';
import { TableCell, Table, TableBody, TableRow, FormControl, MenuItem, Button } from '@mui/material';
import { withStyles } from '@mui/styles';
import UndoIcon from '@mui/icons-material/Undo';
import PropTypes from 'prop-types';
import { formatDate } from 'utils/time.util';
import { visualChecklistFields } from 'views/entry/vc/components/VcTable.util';
import { RadSelect } from 'presentational/Inputs';

const styles = (theme) => ({
  noPadding: {
    padding: 0,
  },
  tableCellStyle: {
    padding: 0,
    textAlign: 'center',
    height: '32px',
    fontSize: '0.75rem',
  },
  table: {
    width: '90px',
  },
  formControl: {
    margin: 0,
  },
  select: {
    fontSize: '14px',
  },
  outValue: {
    '&$error': {
      color: theme.palette.primary.red,
    },
    '& fieldset': {
      borderColor: theme.palette.primary.red,
      color: theme.palette.primary.red,
    },
    borderColor: theme.palette.primary.red,
    color: theme.palette.primary.red,
    borderRadius: '5px',
    fontWeight: 'bold',
    textAlign: 'center',
  },
  menuItem: {
    fontSize: '12px',
  },
  buttonCell: {
    height: '32px',
  },
  cancelIcon: {
    fontSize: '1.2rem',
    color: theme.palette.primary.main,
  },
  MuiSelect: {
    fontSize: '0.75rem',
  },
});

function VcEntryForm(props) {
  const { classes, entryData, click } = props;
  const { values, setFieldValue } = useFormikContext();

  const entryIndex = entryData._id || 'new';

  const handleChange = (e) => {
    const field = e.target.name.substring(e.target.name.lastIndexOf('.') + 1);
    const entry = values.entries[entryIndex];
    entry[field] = e.target.value;
    if (entry[field] === 'fail') {
      if (!entry.outFields.includes(field)) entry.outFields.push(field);
    } else {
      entry.outFields = entry.outFields.filter((currfield) => currfield !== field);
    }
    setFieldValue(`entries.${entryIndex}`, entry);
  };

  const getStyleForField = (field, defaultClass) =>
    values.entries[entryIndex].outFields.includes(field) ? clsx(defaultClass, classes.outValue) : defaultClass;

  let cancelButton = null;
  if (click) {
    cancelButton = (
      <Button onClick={click}>
        <UndoIcon className={classes.cancelIcon} />
      </Button>
    );
  }

  return (
    <TableCell classes={{ root: classes.noPadding }}>
      <Table className={classes.table}>
        <TableBody>
          <TableRow>
            <TableCell className={classes.tableCellStyle}>{formatDate(entryData.date)}</TableCell>
          </TableRow>

          {visualChecklistFields.map((field) => (
            <TableRow key={field}>
              <TableCell className={classes.tableCellStyle}>
                <FormControl className={classes.formControl}>
                  <RadSelect
                    name={`entries.${entryIndex}.${field}`}
                    onChange={(e) => handleChange(e)}
                    className={getStyleForField(field, classes.select)}
                    classes={{ select: classes.MuiSelect }}
                  >
                    <MenuItem className={classes.menuItem} value="n/a">
                      N/A
                    </MenuItem>
                    <MenuItem className={classes.menuItem} value="pass">
                      P
                    </MenuItem>
                    <MenuItem className={classes.menuItem} value="fail">
                      F
                    </MenuItem>
                  </RadSelect>
                  <ErrorMessage name={field} />
                </FormControl>
              </TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TableCell className={classes.tableCellStyle}>{entryData.initials}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell className={classes.tableCellStyle}>
              {entryData?.reviewedBy ? `${entryData?.reviewedBy} on ${formatDate(entryData?.reviewedDate)}` : null}
            </TableCell>
          </TableRow>
          {click && (
            <TableRow>
              <TableCell className={classes.tableCellStyle}>{cancelButton}</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableCell>
  );
}

VcEntryForm.propTypes = {
  entryData: PropTypes.objectOf(PropTypes.any).isRequired,
  click: PropTypes.func,
};

VcEntryForm.defaultProps = {
  click: null,
};

export default withStyles(styles)(VcEntryForm);
